import { useState } from "react";

import type { BuyerCombinedIntroCopyDto } from "@/api/buyers";
import { Button } from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { useCopyCombinedIntro } from "@/hooks/buyers/useCombinedIntro";

import { CopyField } from "../introEmail/IntroEmailCopyModal";

/**
 * The specialist's own mail client for a combined intro — the second half of
 * the dual send, as the single intro has it.
 *
 * <p>🔴 <b>Taking the copy RECORDS THE SEND, on every listing in it.</b> So the
 * request is a mutation behind an explicit button, never fetched on open and
 * never retried: every call records a contact. Unlike the single intro, the
 * server records ALL of it before handing the text over — a copy nobody
 * recorded would be an uncounted contact on N listings at once.</p>
 */
export function CombinedIntroCopyModal({
  buyerLeadId,
  draftId,
  buyerName,
  buyerEmail,
  listingCount,
  body,
  includeListingLinks,
  expectedSubmissionIds,
  onCopied,
  onClose,
}: {
  buyerLeadId: string;
  draftId: string;
  buyerName: string;
  buyerEmail: string | null;
  listingCount: number;
  /** The message on screen — what the copy carries, as a Phoenix send would. */
  body: string;
  includeListingLinks: boolean;
  expectedSubmissionIds: string[];
  /** Called once the send is recorded, so the host can clear its selection. */
  onCopied: (copy: BuyerCombinedIntroCopyDto) => void;
  onClose: () => void;
}) {
  const [copy, setCopy] = useState<BuyerCombinedIntroCopyDto | null>(null);
  const take = useCopyCombinedIntro(buyerLeadId);

  return (
    <Modal
      open
      onClose={onClose}
      title="Send the intro email yourself"
      size="lg"
      footer={
        <div className="flex justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose}>
            {copy ? "Done" : "Cancel"}
          </Button>
          {copy ? null : (
            <Button
              onClick={() =>
                take.mutate(
                  {
                    id: draftId,
                    body: { body, includeListingLinks, expectedSubmissionIds },
                  },
                  {
                    onSuccess: (d) => {
                      setCopy(d);
                      onCopied(d);
                    },
                  },
                )
              }
              disabled={take.isPending}
              data-testid="combined-intro-copy-confirm"
            >
              {take.isPending ? "Preparing…" : "Get the copy and record it as sent"}
            </Button>
          )}
        </div>
      }
    >
      {copy ? (
        <div className="grid gap-space-4">
          <p className="text-small text-onyx m-0">
            Recorded as sent. Paste this into your own email and send it to the
            buyer.
          </p>

          <CopyField label="To" value={copy.toEmail} />
          <CopyField label="Subject" value={copy.subject} />
          <CopyField label="Message" value={copy.body} multiline />

          <p className="text-caption text-gray-mid m-0">
            No signature is included — your own email will add yours.
          </p>
        </div>
      ) : (
        <div className="grid gap-space-4">
          <p className="text-small text-onyx m-0">
            Phoenix will not send anything — you will, from your own email to{" "}
            <strong>{buyerName}</strong>
            {buyerEmail ? (
              <>
                {" "}
                at <strong>{buyerEmail}</strong>
              </>
            ) : null}
            .
          </p>
          <p className="text-small text-status-warning m-0">
            Taking the copy records this intro email as sent on all {listingCount}{" "}
            listings, exactly as sending from Phoenix does — so the buyer does not
            look uncontacted. It does not change any listing&apos;s status.
          </p>
          {take.error ? (
            <p role="alert" className="text-small text-status-error m-0">
              {take.error.message}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
